import React from 'react';
import {defineMessages, type MessageDescriptor} from 'react-intl';
import {
    DateRangeFilter,
    type DateRangeFilterValue,
    FilterGroupNew,
    type FilterGroupNewFilter,
    type FilterProps,
    type FiltersProps,
    type MultiKeyFilterTextValue,
    TextInputWithDropdownFilter,
} from '@igp/shared/components/filter';
import {useLocale} from '@igp/shared/localization';
import type {Locale} from '@igp/shared/style';

import {firstAvailableDate} from '../constants';
import type {AgentLevelReportV2FilterModel, AgentLevelReportV2FilterName, AgentLevelReportV2ServerTextFilterKeys} from '../types';

const localized = defineMessages({
    textFilterPlaceholder: {
        id: 'AgentLevelReportV2Filters_textFilterPlaceholder',
        defaultMessage: 'Search in Agent Level Report',
    },
    texyAgentUid: {
        id: 'AgentLevelReportV2Filters_textAgentUId',
        defaultMessage: 'Agent ID',
    },
    textAgentUsername: {
        id: 'AgentLevelReportV2Filters_textAgentUsername',
        defaultMessage: 'Username',
    },
    textFullFilter: {
        id: 'AgentLevelReportV2Filters_textFullFilter',
        defaultMessage: 'All',
    },
});

type AgentLevelReportV2FiltersProps = FiltersProps<AgentLevelReportV2FilterModel, AgentLevelReportV2FilterName>;

export function AgentLevelReportV2Filters({model, onChange, availableFilters}: AgentLevelReportV2FiltersProps) {
    const availableDateRangeForSelection = {from: firstAvailableDate.toDate()};
    const {locale} = useLocale();
    const placeholderMapper: Record<AgentLevelReportV2ServerTextFilterKeys, MessageDescriptor> = {
        agent_uid: localized.texyAgentUid,
        agent_username: localized.textAgentUsername,
        agent_uid_username: localized.textFullFilter,
    };

    const filters: FilterGroupNewFilter<AgentLevelReportV2FilterModel, AgentLevelReportV2FilterName>[] = [
        {
            filterName: 'agentUidUsername',
            modelField: 'text',
            component: (props: FilterProps<MultiKeyFilterTextValue<AgentLevelReportV2ServerTextFilterKeys>>) => (
                <TextInputWithDropdownFilter
                    options={[
                        {label: placeholderMapper.agent_uid_username, value: 'agent_uid_username'},
                        {label: placeholderMapper.agent_uid, value: 'agent_uid'},
                        {label: placeholderMapper.agent_username, value: 'agent_username'},
                    ]}
                    inputPlaceholder={localized.textFilterPlaceholder}
                    width="full"
                    {...props}
                />
            ),
            collapseOnMobile: false,
        },
        {
            filterName: 'agentUid',
            modelField: 'text',
            component: (props: FilterProps<MultiKeyFilterTextValue<AgentLevelReportV2ServerTextFilterKeys>>) => (
                <TextInputWithDropdownFilter
                    options={[{label: placeholderMapper.agent_uid, value: 'agent_uid'}]}
                    inputPlaceholder={localized.textFilterPlaceholder}
                    width="full"
                    {...props}
                />
            ),
            collapseOnMobile: false,
        },
        {
            filterName: 'weekStartDate',
            modelField: 'weekStartDate',
            component: (props: FilterProps<DateRangeFilterValue>) => (
                <DateRangeFilter
                    {...props}
                    availableDateRangeForSelection={availableDateRangeForSelection}
                    width="full"
                    locale={locale as Locale}
                    filterEntity="report"
                    hasInputTooltip={true}
                />
            ),
            collapseOnMobile: false,
        },
        {
            filterName: 'dailyAggregationRange',
            modelField: 'dailyAggregationRange',
            component: (props: FilterProps<DateRangeFilterValue>) => (
                <DateRangeFilter
                    {...props}
                    availableDateRangeForSelection={availableDateRangeForSelection}
                    width="full"
                    locale={locale as Locale}
                    filterEntity="report"
                    hasInputTooltip={true}
                />
            ),
            collapseOnMobile: false,
        },
    ];

    return (
        <FilterGroupNew
            model={model}
            onChange={onChange}
            mode="update-on-change"
            allFilters={filters}
            availableFilters={availableFilters}
        />
    );
}
